import { createTheme, alpha } from '@mui/material/styles';

// Theme based on Luxury + High-End + Financial Times Style brief

const theme = createTheme({
  palette: {
    mode: 'light', // Ensuring light mode as base
    primary: {
      main: '#002B45', // Deep Navy (Accent)
    },
    secondary: {
      main: '#C2B280', // Muted Gold (Alternative Accent, can be used for secondary actions)
    },
    background: {
      default: '#F9F8F6', // Off-white, non-reflective
      paper: '#FFFFFF',    // For cards, dialogs, etc.
    },
    text: {
      primary: '#1C1C1C',
      secondary: '#555555',
    },
    divider: '#E6E6E6', // Border / divider
    error: {
      main: '#D32F2F', // Standard MUI red, can be adjusted to oxblood if preferred for errors
    },
  },
  typography: {
    fontFamily: 'Inter, Lora, "Source Serif Pro", Arial, sans-serif', // Base font preference
    h1: {
      fontFamily: '"Playfair Display", "Libre Baskerville", serif',
      fontWeight: 500, // Reduced from 600
      fontSize: '2.8rem', // Default is ~3rem for h2, MUI h1 is ~6rem
      letterSpacing: '-0.2px',
    },
    h2: {
      fontFamily: '"Playfair Display", "Libre Baskerville", serif',
      fontWeight: 500, // Reduced from 600
      fontSize: '2.2rem', // Default is ~2.25rem for h3, MUI h2 is ~3.75rem
      letterSpacing: '-0.2px',
    },
    h3: {
      fontFamily: '"Playfair Display", "Libre Baskerville", serif',
      fontWeight: 500, // Reduced from 600
      fontSize: '1.8rem', // Default is ~1.5rem for h4, MUI h3 is ~3rem
      letterSpacing: '-0.2px',
    },
    h4: {
      fontFamily: '"Playfair Display", "Libre Baskerville", serif',
      fontWeight: 500,
      letterSpacing: '-0.1px',
    },
    h5: {
      fontFamily: '"Playfair Display", "Libre Baskerville", serif',
      fontWeight: 500,
      letterSpacing: '-0.1px',
    },
    h6: {
      fontFamily: '"Playfair Display", "Libre Baskerville", serif',
      fontWeight: 500,
      letterSpacing: '0px',
    },
    body1: {
      fontFamily: 'Inter, Lora, "Source Serif Pro", sans-serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: 'Inter, Lora, "Source Serif Pro", sans-serif',
      fontWeight: 300, // Lighter for less emphasis if Lora/Source Serif Pro used
    },
    button: {
      fontWeight: 500,
      textTransform: 'none', // Common for a more refined look
    },
  },
  shape: {
    borderRadius: 6, // Default border radius, can be overridden per component
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({ // Common styles for all buttons
          fontWeight: 500,
          borderRadius: '6px',
          padding: '10px 20px',
          textTransform: 'none', // As per typography.button
        }),
        // --- Text Buttons --- 
        text: ({ ownerState, theme }) => ({
          backgroundColor: 'transparent',
          color: ownerState.color === 'inherit' 
            ? 'inherit' // Will inherit from parent, e.g., AppBar
            : ownerState.color === 'primary'
            ? theme.palette.primary.main
            : ownerState.color === 'secondary'
            ? theme.palette.secondary.main
            : theme.palette.text.primary, // Default text color for text buttons
          '&:hover': {
            backgroundColor: ownerState.color === 'inherit'
              ? 'transparent' // Keep transparent for header-like buttons
              : alpha(ownerState.color === 'primary' ? theme.palette.primary.main 
                      : ownerState.color === 'secondary' ? theme.palette.secondary.main 
                      : theme.palette.text.primary, theme.palette.action.hoverOpacity),
            // For header buttons, specific hover color is handled in Header.js sx prop
          },
        }),
        // --- Contained Buttons --- 
        contained: ({ ownerState, theme }) => ({
          backgroundColor: ownerState.color === 'primary'
            ? theme.palette.primary.main // Deep Navy for primary contained
            : ownerState.color === 'secondary'
            ? theme.palette.secondary.main // Muted Gold for secondary contained
            : '#1A1A1A', // Stone black for default contained
          color: ownerState.color === 'primary'
            ? theme.palette.getContrastText(theme.palette.primary.main)
            : ownerState.color === 'secondary'
            ? theme.palette.getContrastText(theme.palette.secondary.main)
            : '#FFFFFF', // White text for stone black
          '&:hover': {
            backgroundColor: ownerState.color === 'primary'
              ? theme.palette.primary.dark
              : ownerState.color === 'secondary'
              ? theme.palette.secondary.dark
              : '#333333', // Darken stone black
          },
        }),
        // --- Outlined Buttons --- 
        outlined: ({ ownerState, theme }) => ({
          borderColor: ownerState.color === 'primary'
            ? theme.palette.primary.main
            : ownerState.color === 'secondary'
            ? theme.palette.secondary.main
            : '#1A1A1A', // Stone black border for default outlined
          color: ownerState.color === 'primary'
            ? theme.palette.primary.main
            : ownerState.color === 'secondary'
            ? theme.palette.secondary.main
            : '#1A1A1A', // Stone black text for default outlined
          '&:hover': {
            backgroundColor: alpha(
              ownerState.color === 'primary' ? theme.palette.primary.main 
              : ownerState.color === 'secondary' ? theme.palette.secondary.main 
              : '#1A1A1A', 
              theme.palette.action.hoverOpacity
            ),
            borderColor: ownerState.color === 'primary'
            ? theme.palette.primary.dark
            : ownerState.color === 'secondary'
            ? theme.palette.secondary.dark
            : '#000000',
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#DDDDDD',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#AAAAAA',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#002B45', // Accent color on focus
          },
          borderRadius: '4px',
        },
        input: {
          padding: '12px 14px', // MUI default is 16.5px 14px, adjusting for 12px target
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0, // Default to no shadow for a flatter look
      },
      styleOverrides: {
        root: {
          // For cards that need a shadow as per brief: 'rgba(0, 0, 0, 0.05)'
          // This can be applied via sx prop or a custom Paper variant if needed often
        },
        outlined: {
            borderColor: '#E6E6E6',
        }
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          // General table styles
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontFamily: '"Playfair Display", "Libre Baskerville", serif',
            fontWeight: 600, // Header font weight
            color: '#1C1C1C', // Primary text color
            borderBottom: '2px solid #1C1C1C', // Stronger horizontal rule for header
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:nth-of-type(odd)': {
            backgroundColor: '#FAFAFA', // Zebra striping
          },
          '& .MuiTableCell-root': {
            fontFamily: 'Inter, Lora, "Source Serif Pro", sans-serif',
            fontWeight: 400,
            borderBottom: '1px solid #E6E6E6', // Horizontal rule for body rows
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderLeft: 'none', // No vertical rules
          borderRight: 'none', // No vertical rules
          padding: '12px 16px', // Adjust padding for cell content
        },
        head: {
           borderLeft: 'none', 
           borderRight: 'none',
        }
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF', // White app bar for a clean look
          color: '#1C1C1C',         // Primary text color for app bar content
          boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05)', // Subtle shadow
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1C1C1C',
          color: '#FFFFFF',
          borderRadius: '4px',
          fontSize: '0.8rem',
        },
        arrow: {
          color: '#1C1C1C',
        },
      },
    },
  },
});

export default theme;

